import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import { useContext, useState } from "react";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import { toast } from "react-hot-toast";
import {
  getScoringCriteriaErrors,
  hasFiltersApplied,
} from "../curatedSearchUtils";
import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";
import CuratedSearchCardHeader from "../CuratedSearchCard/CuratedSearchCardHeader";
import { OpensearchCuratedSearch } from "../../../__generated__/graphql";
import CuratedInternalSearchPowerUserFilters from "./PowerUserFilters/CuratedInternalSearchPowerUserFilters";
import PowerUserFiltersContext from "../../../contexts/PowerUserFiltersContext";
import powersearchRuleValidator from "./PowerUserFilters/powersearchRuleValidator";

interface ICuratedInternalSearchFilterCard {
  onSave: () => Promise<void>;
  curatedSearchId: number | null;
  scoringCriteriaValues: string[];
  hideHeader?: boolean;
  setSavedFilters: (value: OpensearchCuratedSearch | null) => void;
  savedFilters: OpensearchCuratedSearch | null;
}

const CuratedInternalSearchFilterCard = ({
  onSave,
  curatedSearchId,
  scoringCriteriaValues,
  hideHeader = false,
  savedFilters,
  setSavedFilters,
}: ICuratedInternalSearchFilterCard) => {
  const { upsertAutopilotCuratedSearchLoading } = useContext(
    AutopilotCuratedSearchContext,
  );

  const scoringCriteriaErrors = getScoringCriteriaErrors(scoringCriteriaValues);

  const hasScoringCriteriaErrors = scoringCriteriaErrors.some(
    (error) => error !== null,
  );

  const [showErrorMessages, setShowErrorMessages] = useState(false);

  return (
    <div>
      {!hideHeader && (
        <CuratedSearchCardHeader
          headerText="Filters"
          headerIcon={
            <AdjustmentsVerticalIcon className="h-5 w-5 text-orange-500" />
          }
        />
      )}
      <PowerUserFiltersContext.Provider
        value={{
          showErrorMessages,
          setShowErrorMessages,
        }}
      >
        <CuratedInternalSearchPowerUserFilters
          savedFilters={savedFilters}
          setSavedFilters={setSavedFilters}
        />
      </PowerUserFiltersContext.Provider>
      <div className={classNames("flex", "justify-end", "gap-2", "pt-2")}>
        <CiroButton
          analyticsField="Save Internal Ciro Database Filters"
          style={CiroButtonStyleEnum.LOUD}
          disabled={
            !hasFiltersApplied({ filters: savedFilters, type: "internal" }) ||
            upsertAutopilotCuratedSearchLoading
            // Note: hasScoringCriteriaErrors is removed to allow clicking with scoring criteria errors
          }
          onClick={async () => {
            if (!savedFilters) {
              toast.error("No filters applied");
              return;
            }
            const validationErrors = powersearchRuleValidator(
              JSON.parse(savedFilters.opensearchFiltersBlob),
            );

            if (Object.keys(validationErrors).length > 0) {
              setShowErrorMessages(true);
              toast.error("Please fix errors in filters");
              return;
            }

            if (hasScoringCriteriaErrors) {
              toast.error("Please fix errors in scoring criteria");
              return;
            }
            await onSave();
          }}
        >
          {Boolean(curatedSearchId) ? "Regenerate preview" : "Preview results"}
        </CiroButton>
      </div>
    </div>
  );
};

export default CuratedInternalSearchFilterCard;
